import {
  BookedExternalPlaceItem,
  FlightItineraryItem,
  PlaceItem,
  UnknownEntityItem,
  WebSearchResultItem,
} from "@/types/add-to-trip"
import { DayObject, TimeOfDay } from "@/types/date"
import { PlaceRef } from "@/types/entity"
import {
  ItineraryDay,
  ItineraryEvent,
  ItineraryEventType,
  ItineraryItemType,
} from "@/types/itinerary"
import { Place } from "@/types/place"
import {
  LimitedTripTabs,
  TimeFrame,
  TravelerGroup,
  Trip,
  TripRef,
  TripStop,
  TripTab,
  TripTabItinerary,
} from "@/types/trip"
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit"

import { browserTimeZone } from "../date-utils"
import { dayRangeForTimeFrame } from "../time-frame-utils"
import { DEFAULT_TRAVELERS } from "../trip-utils"
import { selectChatTripIsUserCreatedFromChatsCache } from "./api/chats-api"
import { savedPlacesApi } from "./api/saved-places-api"
import { tripsApi } from "./api/trips-api"
import { RootState } from "./redux-store"
import { selectLimitTripFeatures } from "./ui-settings-slice"
import userSlice, {
  selectChatId,
  selectChatTrip as selectLoadedChatTrip,
} from "./user-slice"

const selectChatTrip = (state: RootState) =>
  state.userSlice.chat.id !== 0 ? state.userSlice.chat.trip : undefined

export type OpenNezasaDialogProps = {
  nezasaReference: string
  packageLanguage?: string
  packageCountryCode?: string
}

// Explicit prefill data for creating a trip (from receipts, etc.)
export type TripPrefillData = {
  destination_entity_ref?: PlaceRef
  major_stops?: TripStop[]
  time_frame?: TimeFrame
  travelers?: TravelerGroup | null
}

type CreateTripDialogState = {
  open: boolean
  config: CreateTripDialogConfig | undefined
}

export type CreateTripDialogItemToAdd =
  | PlaceItem
  | BookedExternalPlaceItem
  | WebSearchResultItem
  | UnknownEntityItem
  | FlightItineraryItem

export type CreateTripDialogConfig = {
  itemToAdd?: CreateTripDialogItemToAdd
  // Callback ID is used to look up callbacks in tripDialogCallbackRegistry.
  // This works around Redux's inability to store non-serializable functions.
  callbackId?: string
  /** After creating the trip, start a chat with the form prompt and navigate to it */
  startChat?: boolean
  /** After creating the trip, navigate to its trip page */
  goToTrip?: boolean
  analyticsSource?: "home_vertical"
  showSheet?: boolean
  // When true, user must select specific dates (can't choose "Flexible")
  requireDates?: boolean
  // Pre-fill data for the trip form:
  // - "current": use the current trip's data
  // - object: explicit prefill data (from receipts, etc.)
  // - undefined: start fresh
  prefillTripData?: "current" | TripPrefillData
}

export interface HighlightedEvent {
  id: number | undefined
  type: ItineraryItemType | undefined
}

export type SchedulingMode = "schedule" | "duplicate"

export interface SchedulingPayload {
  event: ItineraryEvent
  mode: SchedulingMode
}

export interface AddEventDialogState {
  open: boolean
  // For itinerary day context (from trip-itinerary-day)
  itineraryDay?: ItineraryDay
  // For calendar context (specific day/time selection)
  day?: DayObject
  // A drag can span days, so the end has its own day, not just a time.
  endDay?: DayObject
  dayNumber?: number
  startTime?: TimeOfDay
  endTime?: TimeOfDay
}

export interface TripSliceState {
  showNezasaDialog: boolean
  showTuiPreviewDialog: boolean
  nezasaReference: string | undefined
  packageLanguage: string | undefined
  packageCountryCode: string | undefined
  createTripDialogState: CreateTripDialogState | undefined
  currentTab: TripTab
  dayTitleDialog: {
    open: boolean
    dayNumber?: number
    day?: DayObject
    initialTitle?: string
  }
  dayNoteDialog: {
    open: boolean
    dayNumber?: number
    day?: DayObject
    initialNote?: string
  }
  highlightedEvent: HighlightedEvent
  schedulingEvent: SchedulingPayload | undefined
  commentingEvent: ItineraryEvent | undefined
  editingItineraryEvent: ItineraryEvent | undefined
  eventUnscheduled: boolean
  unscheduledFilterType: ItineraryEventType | "all"
  unscheduledLocationFilter: PlaceRef | "all"
  editingDetailId: number | undefined
  tripRef: TripRef | null
  tripDetailsDialogOpen: boolean
  loadingTripRef: TripRef | undefined
  lastUsedTripRef: TripRef | undefined
  addEventDialog: AddEventDialogState
}

export const tripSliceInitialState: TripSliceState = {
  showNezasaDialog: false,
  showTuiPreviewDialog: false,
  nezasaReference: undefined,
  packageLanguage: undefined,
  packageCountryCode: undefined,
  createTripDialogState: undefined,
  currentTab: "itinerary",
  dayTitleDialog: {
    open: false,
  },
  dayNoteDialog: {
    open: false,
  },
  highlightedEvent: {
    id: undefined,
    type: undefined,
  },
  schedulingEvent: undefined,
  commentingEvent: undefined,
  editingItineraryEvent: undefined,
  eventUnscheduled: false,
  unscheduledFilterType: "all",
  unscheduledLocationFilter: "all",
  editingDetailId: undefined,
  tripRef: null,
  tripDetailsDialogOpen: false,
  loadingTripRef: undefined,
  lastUsedTripRef: undefined,
  addEventDialog: {
    open: false,
  },
}

const selectSliceState = (state: RootState) => state.tripSlice

export const selectNezasaReference = createSelector(
  [selectSliceState],
  (state) => {
    return state.nezasaReference
  }
)

export const selectPackageLanguage = createSelector(
  [selectSliceState],
  (state) => {
    return state.packageLanguage
  }
)

export const selectPackageCountryCode = createSelector(
  [selectSliceState],
  (state) => {
    return state.packageCountryCode
  }
)

export const selectShowNezasaDialog = createSelector(
  [selectSliceState],
  (state) => {
    return state.showNezasaDialog
  }
)

export const selectShowTuiPreviewDialog = createSelector(
  [selectSliceState],
  (state) => {
    return state.showTuiPreviewDialog
  }
)
export const selectCreateTripDialogState = createSelector(
  [selectSliceState],
  (state) => state.createTripDialogState
)

export const selectCreateTripDialogOpen = createSelector(
  [selectCreateTripDialogState],
  (state) => state?.open ?? false
)

export const selectCreateTripDialogConfig = createSelector(
  [selectCreateTripDialogState],
  (state) => state?.config
)

export const selectCurrentTab = createSelector(
  [selectSliceState, selectLimitTripFeatures],
  (state, limited): TripTab =>
    limited && !LimitedTripTabs.includes(state.currentTab)
      ? TripTabItinerary
      : state.currentTab
)

export const selectDayTitleDialog = createSelector(
  [selectSliceState],
  (state) => state.dayTitleDialog
)

export const selectDayNoteDialog = createSelector(
  [selectSliceState],
  (state) => state.dayNoteDialog
)

export const selectHighlightedEvent = createSelector(
  [selectSliceState],
  (state) => state.highlightedEvent
)

export const selectHighlightedEventId = createSelector(
  [selectHighlightedEvent],
  (event) => event.id
)

export const selectSchedulingEvent = createSelector(
  [selectSliceState],
  (state) => state.schedulingEvent?.event
)

export const selectSchedulingMode = createSelector(
  [selectSliceState],
  (state) => state.schedulingEvent?.mode ?? "schedule"
)

export const selectCommentingEvent = createSelector(
  [selectSliceState],
  (state) => state.commentingEvent
)

export const selectEditingItineraryEvent = createSelector(
  [selectSliceState],
  (state) => state.editingItineraryEvent
)

export const selectEventUnscheduled = createSelector(
  [selectSliceState],
  (state) => state.eventUnscheduled
)

export const selectUnscheduledFilterType = createSelector(
  [selectSliceState],
  (state) => state.unscheduledFilterType
)

export const selectUnscheduledLocationFilter = createSelector(
  [selectSliceState],
  (state) => state.unscheduledLocationFilter
)

export const selectEditingDetailId = createSelector(
  [selectSliceState],
  (state) => state.editingDetailId
)

export const selectTripRef = createSelector(
  [selectSliceState, selectChatTrip],
  (state, chatTrip): TripRef | null => {
    return state.tripRef || chatTrip?.ref || null
  }
)

/** Trip ref from the active chat only (ignores trip-slice tripRef). */
export const selectChatTripRef = createSelector(
  [selectChatTrip],
  (chatTrip): TripRef | null => chatTrip?.ref ?? null
)

// selectTrip reads from RTK Query cache using tripRef.
// Falls back to chat.trip for streaming updates that haven't been synced to cache yet.
export const selectTrip = createSelector(
  [selectTripRef, selectChatTrip, (state: RootState) => state],
  (tripRef, chatTrip, state): Trip | undefined => {
    if (!tripRef) return undefined

    const cachedTrip = tripsApi.endpoints.getTrip.select(tripRef)(state)?.data

    if (cachedTrip) {
      return cachedTrip
    }

    // Fall back to chat.trip for:
    // 1. Streaming updates that update chat.trip before cache is synced
    // 2. Initial load before RTK Query fetch completes
    if (chatTrip?.ref === tripRef) {
      return chatTrip
    }

    return undefined
  }
)

export const selectTripId = createSelector([selectTrip], (trip) => trip?.id)

export const selectTripDetailsDialogOpen = createSelector(
  [selectSliceState],
  (state) => state.tripDetailsDialogOpen
)

export const selectLoadingTripRef = createSelector(
  [selectSliceState],
  (state) => state.loadingTripRef
)

export const selectLastUsedTripRef = createSelector(
  [selectSliceState],
  (state) => state.lastUsedTripRef
)

export const selectTripOwner = createSelector(
  [selectTrip],
  (trip) => trip?.owner
)

export const selectTripTimeZone = createSelector(
  [selectTrip],
  (trip) => trip?.time_zone ?? browserTimeZone()
)

export const selectTripDestination = createSelector(
  [selectTrip],
  (trip) => trip?.destination
)

export const selectTripDestinationEntityRef = createSelector(
  [selectTrip],
  (trip) => trip?.destination?.entity_ref ?? trip?.destination_entity_ref
)

export const selectTripStopsAreaEntityRef = createSelector(
  [selectTrip],
  (trip) => trip?.stops_area?.entity_ref ?? trip?.stops_area_entity_ref
)

export const selectTripHasDestination = createSelector(
  [selectTrip],
  (trip) => !!trip?.destination
)

export const selectTripIsUserCreated = createSelector(
  [selectTrip],
  (trip) => trip?.user_created
)

/**
 * Whether the current chat conversation belongs to a user-created trip, for site
 * nav highlighting. Uses the chats list cache when Redux trip state is not yet
 * available (e.g. trip page unmount clears tripRef before the chat fetch finishes).
 */
export function selectTripIsUserCreatedForNav(
  state: RootState,
  chatIdFromPath: number | null
): boolean {
  if (chatIdFromPath) {
    if (selectChatTripIsUserCreatedFromChatsCache(state, chatIdFromPath)) {
      return true
    }

    const loadedChatId = selectChatId(state)
    if (loadedChatId === chatIdFromPath) {
      const chatTrip = selectLoadedChatTrip(state)
      const trip = selectTrip(state)
      return Boolean(chatTrip?.user_created ?? trip?.user_created)
    }

    return false
  }

  return Boolean(selectTripIsUserCreated(state))
}

export const selectTripIsCollaboration = createSelector(
  [selectTrip],
  (trip) => trip?.collaboration
)

export const selectTripMajorStopPlaceRefs = createSelector(
  [selectTrip],
  (trip) =>
    trip?.major_stops?.map((stop) => stop.entity_ref).filter(Boolean) || []
)

export const selectIsRoadTrip = createSelector(
  [selectTrip],
  (trip) => trip?.roadtrip
)

export const selectIsRouteEnabled = createSelector([selectTrip], (trip) =>
  Boolean(trip?.roadtrip && trip?.major_stops?.length)
)

export const selectTripMajorStopsDetails = createSelector(
  [selectTrip],
  (trip) => trip?.major_stops_details || []
)

export const selectTripDraftStartDate = createSelector(
  [selectTrip],
  (trip) => trip?.draft_start_date
)

export const selectTripDuration = createSelector(
  [selectTrip],
  (trip) => trip?.draft_duration
)

export const selectTripTimeFrame = createSelector(
  [selectTrip],
  (trip) => trip?.time_frame
)

export const selectTripDraftDuration = createSelector(
  [selectTrip],
  (trip) => trip?.draft_duration
)

export const selectTripTravelers = createSelector(
  [selectTrip],
  (trip) => trip?.travelers
)

export const selectTripTravelersOrDefault = createSelector(
  [selectTripTravelers],
  (travelers) => travelers ?? DEFAULT_TRAVELERS
)

export const selectTripDates = createSelector(
  [selectTripTimeFrame],
  (timeFrame) => dayRangeForTimeFrame(timeFrame)
)

const EMPTY_SAVED_PLACES: Place[] = []

export const selectTripSavedPlaces = createSelector(
  [selectTripRef, selectTripIsUserCreated, (state: RootState) => state],
  (tripRef, tripIsUserCreated, state) => {
    if (!tripRef || !tripIsUserCreated) return EMPTY_SAVED_PLACES
    const result =
      savedPlacesApi.endpoints.getSavedPlacesByTrip.select(tripRef)(state)
    return result.data?.map((saved) => saved.entity) ?? EMPTY_SAVED_PLACES
  }
)

export const selectAddEventDialog = createSelector(
  [selectSliceState],
  (state) => state.addEventDialog
)

const tripSlice = createSlice({
  name: "trip",
  initialState: tripSliceInitialState,
  reducers: {
    reset: () => tripSliceInitialState,
    setNezasaReference: (state, action: PayloadAction<string | undefined>) => {
      state.nezasaReference = action.payload
    },
    openNezasaDialog: (state, action: PayloadAction<OpenNezasaDialogProps>) => {
      state.nezasaReference = action.payload?.nezasaReference
      state.packageLanguage = action.payload?.packageLanguage || "en"
      state.packageCountryCode = action.payload?.packageCountryCode || "US"
      state.showNezasaDialog = true
    },
    closeNezasaDialog: (state) => {
      state.nezasaReference = undefined
      state.showNezasaDialog = false
    },
    openTuiPreviewDialog: (state) => {
      state.showTuiPreviewDialog = true
    },
    closeTuiPreviewDialog: (state) => {
      state.showTuiPreviewDialog = false
    },
    openCreateTripDialog: (
      state,
      action: PayloadAction<CreateTripDialogConfig | undefined>
    ) => {
      state.createTripDialogState = {
        config: action.payload,
        open: true,
      }
    },
    closeCreateTripDialog: (state) => {
      state.createTripDialogState = {
        open: false,
        config: undefined,
      }
    },
    setCurrentTab: (state, action: PayloadAction<TripTab>) => {
      state.currentTab = action.payload
    },
    setDayTitleDialog: (
      state,
      action: PayloadAction<TripSliceState["dayTitleDialog"]>
    ) => {
      state.dayTitleDialog = { ...state.dayTitleDialog, ...action.payload }
    },
    setDayNoteDialog: (
      state,
      action: PayloadAction<TripSliceState["dayNoteDialog"]>
    ) => {
      state.dayNoteDialog = { ...state.dayNoteDialog, ...action.payload }
    },
    setHighlightedEvent: (state, action: PayloadAction<HighlightedEvent>) => {
      state.highlightedEvent = action.payload
    },
    setHighlightedEventId: (
      state,
      action: PayloadAction<number | undefined>
    ) => {
      state.highlightedEvent = {
        id: action.payload,
        type: undefined,
      }
    },
    clearHighlightedEvent: (state) => {
      state.highlightedEvent = {
        id: undefined,
        type: undefined,
      }
    },
    setSchedulingEvent: (
      state,
      action: PayloadAction<ItineraryEvent | undefined>
    ) => {
      state.schedulingEvent = action.payload
        ? { event: action.payload, mode: "schedule" }
        : undefined
    },
    setDuplicatingEvent: (state, action: PayloadAction<ItineraryEvent>) => {
      state.schedulingEvent = { event: action.payload, mode: "duplicate" }
    },
    setCommentingEvent: (
      state,
      action: PayloadAction<ItineraryEvent | undefined>
    ) => {
      state.commentingEvent = action.payload
    },
    setEditingItineraryEvent: (
      state,
      action: PayloadAction<ItineraryEvent | undefined>
    ) => {
      state.editingItineraryEvent = action.payload
    },
    setEventUnscheduled: (state, action: PayloadAction<boolean>) => {
      state.eventUnscheduled = action.payload
    },
    setUnscheduledFilterType: (
      state,
      action: PayloadAction<ItineraryEventType | "all">
    ) => {
      state.unscheduledFilterType = action.payload
    },
    setUnscheduledLocationFilter: (
      state,
      action: PayloadAction<PlaceRef | "all">
    ) => {
      state.unscheduledLocationFilter = action.payload
    },
    setEditingDetailId: (state, action: PayloadAction<number | undefined>) => {
      state.editingDetailId = action.payload
    },
    setTripRef: (state, action: PayloadAction<TripRef>) => {
      const newTripRef = action.payload
      if (state.tripRef && state.tripRef !== newTripRef) {
        return { ...tripSliceInitialState, tripRef: newTripRef }
      }
      state.tripRef = newTripRef
    },
    setTripDetailsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.tripDetailsDialogOpen = action.payload
    },
    setLoadingTripRef: (state, action: PayloadAction<TripRef | undefined>) => {
      state.loadingTripRef = action.payload
    },
    setLastUsedTripRef: (state, action: PayloadAction<TripRef | undefined>) => {
      state.lastUsedTripRef = action.payload
    },
    removeTrip: (state, action: PayloadAction<TripRef>) => {
      const tripRef = action.payload
      if (state.lastUsedTripRef === tripRef) {
        state.lastUsedTripRef = undefined
      }
    },
    openAddEventDialog: (
      state,
      action: PayloadAction<Omit<AddEventDialogState, "open"> | undefined>
    ) => {
      state.addEventDialog = {
        open: true,
        ...action.payload,
      }
    },
    closeAddEventDialog: (state) => {
      state.addEventDialog = {
        open: false,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(userSlice.actions.setChat, (state, action) => {
        // When chat is set, extract tripRef. The full trip data from chat.trip
        // will be synced to RTK Query cache via a middleware/thunk.
        const newTripRef = action.payload.trip?.ref ?? null
        if (state.tripRef && state.tripRef !== newTripRef) {
          return {
            ...tripSliceInitialState,
            tripRef: newTripRef,
            createTripDialogState: state.createTripDialogState,
          }
        }
        state.tripRef = newTripRef
      })
      // The create-trip dialog isn't tied to a chat. /home resets the chat on
      // mount, after the ?search=trip deep link has already opened the dialog.
      .addCase(userSlice.actions.resetChat, (state) => {
        return {
          ...tripSliceInitialState,
          createTripDialogState: state.createTripDialogState,
        }
      })
  },
})

export const {
  setNezasaReference,
  openNezasaDialog,
  closeNezasaDialog,
  openTuiPreviewDialog,
  closeTuiPreviewDialog,
  setCurrentTab,
  setDayTitleDialog,
  setDayNoteDialog,
  setHighlightedEvent,
  setHighlightedEventId,
  clearHighlightedEvent,
  setSchedulingEvent,
  setDuplicatingEvent,
  setCommentingEvent,
  setEditingItineraryEvent,
  setEventUnscheduled,
  setUnscheduledFilterType,
  setUnscheduledLocationFilter,
  setEditingDetailId,
  setTripRef,
  reset,
  setLoadingTripRef,
  setLastUsedTripRef,
  removeTrip,
  openAddEventDialog,
  closeAddEventDialog,
} = tripSlice.actions

export type TripSlice = typeof tripSlice

export default tripSlice
